import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Calendar,
  CheckSquare,
  ListTodo,
  ArrowRight,
  Sparkles,
  Heart,
  Github,
  Star,
  Copy,
  Check,
  GraduationCap,
  Trophy,
} from 'lucide-react';
import { analytics } from '../lib/analytics';
import { useAuthStore } from '../stores/auth';
import { useGithubStars, GITHUB_REPO_URL } from '../hooks/useGithubStars';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { HeroTabletMock } from '../components/landing/HeroTabletMock';
import { riveraMembers } from '../components/landing/riveraDemo';

const DOCKER_SNIPPET = 'docker compose up -d';

function Header() {
  const { isAuthenticated, logout } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await fetch('/api/v1/auth/logout', { method: 'POST' });
    logout();
    navigate('/home');
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/20">
      <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <div className="relative">
            <img
              src="/my-family-calendar-primary-icon.png?v=2"
              alt=""
              className="w-11 h-11 rounded-xl shadow-md transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute -inset-1 bg-teal/20 rounded-xl blur-md opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          <span className="font-display text-xl font-semibold text-text-primary hidden sm:block">
            My Family Calendar
          </span>
        </Link>

        {/* Navigation Links */}
        <div className="hidden md:flex items-center gap-8">
          <a href="#features" className="text-text-secondary hover:text-text-primary transition-colors font-medium">
            Features
          </a>
          <a href="#how-it-works" className="text-text-secondary hover:text-text-primary transition-colors font-medium">
            How It Works
          </a>
          <a href="#own-your-data" className="text-text-secondary hover:text-text-primary transition-colors font-medium">
            Self-Host
          </a>
        </div>

        {/* CTA Buttons */}
        <div className="flex items-center gap-3">
          {isAuthenticated ? (
            <>
              <Link to="/" className="btn btn-primary">
                Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
              <button
                onClick={handleLogout}
                className="text-text-secondary hover:text-text-primary transition-colors font-medium"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="hidden sm:block text-text-secondary hover:text-text-primary transition-colors font-medium"
              >
                Sign In
              </Link>
              <Link
                to="/login"
                onClick={() => analytics.landingCtaClicked('header')}
                className="btn btn-primary"
              >
                Get Started Free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </>
          )}
        </div>
      </nav>
    </header>
  );
}

function HeroSection() {
  const stars = useGithubStars();

  return (
    <section className="landing-hero min-h-screen flex items-center relative pt-20">
      {/* Decorative floating shapes */}
      <div className="floating-shape w-64 h-64 bg-teal/10 -top-20 -left-20" style={{ animationDelay: '0s' }} />
      <div className="floating-shape w-48 h-48 bg-amber/10 top-40 right-20" style={{ animationDelay: '2s' }} />
      <div className="floating-shape w-32 h-32 bg-coral/10 bottom-40 left-1/4" style={{ animationDelay: '4s' }} />

      <div className="max-w-7xl mx-auto px-6 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal/10 border border-teal/20 mb-8 animate-fade-in-down fill-both">
              <Sparkles className="w-4 h-4 text-teal" />
              <span className="text-sm font-medium text-teal-dark">Designed for busy families</span>
            </div>

            {/* Main headline */}
            <h1 className="font-display text-display-lg md:text-display-xl lg:text-display-2xl text-text-primary mb-6 animate-fade-in-up fill-both delay-100">
              Your Household
              <span className="block text-gradient">Command Center</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl text-text-secondary max-w-xl mx-auto lg:mx-0 mb-10 animate-fade-in-up fill-both delay-200">
              Aggregate family calendars, coordinate chores with gamification, and keep everyone on the same page with shared lists.
            </p>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up fill-both delay-300">
              <Link
                to="/login"
                onClick={() => analytics.landingCtaClicked('hero')}
                className="btn btn-primary btn-lg group"
              >
                Get Started Free
                <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href={GITHUB_REPO_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => analytics.landingGithubClicked('hero')}
                className="btn btn-secondary btn-lg"
              >
                <Star className="w-5 h-5 mr-2" aria-hidden="true" />
                Star on GitHub
                {stars !== null && (
                  <span className="ml-2 px-2 py-0.5 rounded-full bg-teal/10 text-teal text-sm font-semibold">
                    {stars.toLocaleString()}
                  </span>
                )}
              </a>
            </div>

            {/* Trust indicators — honest signals, no invented numbers */}
            <div className="mt-12 flex items-center gap-3 justify-center lg:justify-start animate-fade-in fill-both delay-500 text-sm text-text-muted">
              <Heart className="w-4 h-4 text-coral" aria-hidden="true" />
              <span>Open source &middot; MIT licensed &middot; Built in the open</span>
            </div>
          </div>

          {/* Right content — live tablet mock (fictional Rivera demo data) */}
          <div className="relative animate-fade-in-up fill-both delay-200">
            <div className="absolute inset-0 bg-gradient-radial from-teal/20 via-transparent to-transparent blur-3xl" />
            <div className="relative">
              <HeroTabletMock />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 rounded-full border-2 border-text-muted/30 flex justify-center pt-2">
          <div className="w-1.5 h-3 rounded-full bg-text-muted/50" />
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Feature deep-dive rows                                              */
/* ------------------------------------------------------------------ */

function CalendarVisual() {
  return (
    <div className="bg-white rounded-3xl border border-border-light shadow-lg p-4 sm:p-6">
      <div className="grid grid-cols-4 gap-2 sm:gap-3">
        {riveraMembers.map((m) => (
          <div key={m.name} className="min-w-0">
            <div
              className="text-[10px] sm:text-xs font-semibold text-white rounded-t-lg px-2 py-1 text-center truncate"
              style={{ backgroundColor: m.color }}
            >
              {m.name}
            </div>
            <div className="space-y-1.5 bg-bg-warm rounded-b-lg p-1.5 sm:p-2">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="h-5 sm:h-7 rounded-md"
                  style={{ backgroundColor: m.color, opacity: 0.25 + i * 0.15 }}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-text-muted text-center">
        Google Calendar &middot; Outlook &middot; ICS feeds — one view
      </p>
    </div>
  );
}

function ChoresVisual() {
  const rows = [
    { name: 'Zoe', color: '#ea515f', points: 85 },
    { name: 'Ollie', color: '#fea802', points: 70 },
    { name: 'Dev', color: '#1184a7', points: 40 },
  ];
  return (
    <div className="bg-white rounded-3xl border border-border-light shadow-lg p-4 sm:p-6 space-y-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-text-primary">
        <Trophy className="w-4 h-4 text-amber" aria-hidden="true" />
        This week's leaderboard
      </div>
      {rows.map((r, i) => (
        <div key={r.name} className="flex items-center gap-3">
          <span className="text-xs font-bold text-text-muted w-4">{i + 1}</span>
          <span
            className="w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center"
            style={{ backgroundColor: r.color }}
          >
            {r.name[0]}
          </span>
          <div className="flex-1 h-2.5 rounded-full bg-bg-warm overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{ width: `${r.points}%`, backgroundColor: r.color }}
            />
          </div>
          <span className="text-xs font-semibold text-text-secondary w-12 text-right">
            {r.points} pts
          </span>
        </div>
      ))}
    </div>
  );
}

function ListsVisual() {
  const items = [
    { title: 'Milk', checked: false },
    { title: 'Bananas', checked: false },
    { title: 'Dog food', checked: true },
    { title: 'Pasta', checked: false },
  ];
  return (
    <div className="bg-white rounded-3xl border border-border-light shadow-lg p-4 sm:p-6">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-semibold text-text-primary">Groceries</span>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal/10 text-teal font-medium">
          Synced with Google Keep
        </span>
      </div>
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item.title} className="flex items-center gap-2.5 text-sm">
            <span
              className={`w-4 h-4 rounded border flex items-center justify-center ${
                item.checked ? 'bg-teal border-teal' : 'border-border-default'
              }`}
            >
              {item.checked && <Check className="w-3 h-3 text-white" aria-hidden="true" />}
            </span>
            <span className={item.checked ? 'line-through text-text-muted' : 'text-text-secondary'}>
              {item.title}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SchoolVisual() {
  const items = ['Library book', 'Sport uniform', 'Signed permission slip'];
  return (
    <div className="bg-white rounded-3xl border border-border-light shadow-lg p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-7 h-7 rounded-full bg-coral text-white text-xs font-bold flex items-center justify-center">
          Z
        </span>
        <span className="text-sm font-semibold text-text-primary">Zoe — pack for tomorrow</span>
      </div>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={item} className="flex items-center gap-2.5 text-sm">
            <span
              className={`w-4 h-4 rounded border flex items-center justify-center ${
                i === 0 ? 'bg-teal border-teal' : 'border-border-default'
              }`}
            >
              {i === 0 && <Check className="w-3 h-3 text-white" aria-hidden="true" />}
            </span>
            <span className={i === 0 ? 'line-through text-text-muted' : 'text-text-secondary'}>
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface DeepDiveRowProps {
  icon: React.ComponentType<{ className?: string }>;
  accentClass: string;
  title: string;
  description: string;
  bullets: string[];
  visual: React.ReactNode;
  flipped?: boolean;
}

function DeepDiveRow({ icon: Icon, accentClass, title, description, bullets, visual, flipped }: DeepDiveRowProps) {
  const { ref, revealed } = useScrollReveal();

  return (
    <div
      ref={ref}
      className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center ${
        revealed ? 'reveal-shown' : 'reveal-hidden'
      }`}
    >
      <div className={flipped ? 'lg:order-2' : ''}>
        <div className={`feature-icon ${accentClass}`}>
          <Icon className="w-7 h-7" />
        </div>
        <h3 className="font-display text-2xl md:text-3xl font-semibold text-text-primary mb-4">{title}</h3>
        <p className="text-text-secondary leading-relaxed mb-6">{description}</p>
        <ul className="space-y-2">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2.5 text-text-secondary">
              <Check className="w-5 h-5 text-teal shrink-0 mt-0.5" aria-hidden="true" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className={flipped ? 'lg:order-1' : ''}>{visual}</div>
    </div>
  );
}

function FeaturesSection() {
  return (
    <section id="features" className="landing-section landing-section--teal">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <div className="badge badge-teal mb-4">Features</div>
          <h2 className="font-display text-display-md md:text-display-lg text-text-primary mb-6">
            Everything Your Family Needs
          </h2>
          <p className="text-lg text-text-secondary">
            Powerful features designed specifically for busy families who want to stay organized without the complexity.
          </p>
        </div>

        <div className="space-y-20 md:space-y-32">
          <DeepDiveRow
            icon={Calendar}
            accentClass="feature-icon--teal"
            title="Calendar Aggregation"
            description="Connect Google Calendar, Microsoft Outlook, and ICS feeds. See everyone's schedule in one unified view, color-coded per family member."
            bullets={[
              'Automatic sync every few minutes',
              'Day, week, and month views built for a kitchen tablet',
              'Smart deduplication across shared calendars',
            ]}
            visual={<CalendarVisual />}
          />
          <DeepDiveRow
            icon={CheckSquare}
            accentClass="feature-icon--amber"
            title="Chores & Points"
            description="Assign recurring chores, earn points, track streaks, and compete on the family leaderboard. Gamification that actually gets beds made."
            bullets={[
              'Daily and weekly recurring chores',
              'Points, streaks, and a live leaderboard',
              'Confetti when the job gets done',
            ]}
            visual={<ChoresVisual />}
            flipped
          />
          <DeepDiveRow
            icon={ListTodo}
            accentClass="feature-icon--sage"
            title="Shared Lists"
            description="Shopping lists, packing lists, and to-dos the whole family can edit — with two-way Google Keep sync for voice assistant access."
            bullets={[
              'Add milk from your phone, see it on the kitchen display',
              'Two-way Google Keep sync',
              'Groceries, packing, and custom lists',
            ]}
            visual={<ListsVisual />}
          />
          <DeepDiveRow
            icon={GraduationCap}
            accentClass="feature-icon--coral"
            title="School View"
            description="A per-kid 'what to remember' view: what to wear, what to bring, and today's reminders — so mornings run themselves."
            bullets={[
              'One card per child with a progress ring',
              'Evening pack-for-tomorrow mode',
              'Keyword rules turn calendar events into reminders',
            ]}
            visual={<SchoolVisual />}
            flipped
          />
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const steps = [
    {
      number: '01',
      title: 'Connect Your Calendars',
      description: 'Link Google Calendar, Outlook, or any ICS feed. We sync automatically every few minutes.',
      color: 'teal',
    },
    {
      number: '02',
      title: 'Add Family Members',
      description: 'Create profiles for each family member with their own color. Perfect for visual coordination.',
      color: 'coral',
    },
    {
      number: '03',
      title: 'Set Up Chores & Lists',
      description: 'Create recurring chores with points and shared lists. Gamification makes it fun!',
      color: 'amber',
    },
  ];

  return (
    <section id="how-it-works" className="landing-section landing-section--warm">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="badge badge-coral mb-4">How It Works</div>
          <h2 className="font-display text-display-md md:text-display-lg text-text-primary mb-6">
            Get Started in Minutes
          </h2>
          <p className="text-lg text-text-secondary">
            Setting up your family command center is quick and easy. Here's how to get started.
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, index) => (
            <div
              key={step.number}
              className="relative animate-fade-in-up fill-both"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Connector line (hidden on last item and mobile) */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-12 left-1/2 w-full h-0.5 bg-gradient-to-r from-border-default to-transparent" />
              )}

              <div className="relative bg-white rounded-3xl p-8 border border-border-light shadow-sm hover:shadow-lg transition-shadow">
                {/* Step number */}
                <div
                  className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 font-display text-2xl font-bold text-white"
                  style={{
                    backgroundColor: step.color === 'teal' ? '#15a2a2' : step.color === 'coral' ? '#ea515f' : '#fea802',
                  }}
                >
                  {step.number}
                </div>

                <h3 className="font-display text-xl font-semibold text-text-primary mb-3">
                  {step.title}
                </h3>
                <p className="text-text-secondary leading-relaxed">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function OwnYourDataSection() {
  const [copied, setCopied] = useState(false);
  const { ref, revealed } = useScrollReveal();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(DOCKER_SNIPPET);
      setCopied(true);
      analytics.landingDockerSnippetCopied();
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable — nothing to do.
    }
  };

  return (
    <section id="own-your-data" className="landing-section bg-navy text-white relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '32px 32px',
          }}
        />
      </div>

      <div
        ref={ref}
        className={`max-w-5xl mx-auto px-6 relative z-10 ${revealed ? 'reveal-shown' : 'reveal-hidden'}`}
      >
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-6">
            <Github className="w-4 h-4" aria-hidden="true" />
            <span className="text-sm font-medium">MIT License</span>
          </div>
          <h2 className="font-display text-display-md md:text-display-lg mb-4">Own Your Data</h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            My Family Calendar is open source. Self-host it on your own hardware, or sign up at
            myfamilycalendar.app — either way, your family's data stays yours.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="bg-white/5 border border-white/10 rounded-3xl p-6">
            <p className="text-sm text-white/60 mb-3">Run it yourself in one command:</p>
            <div className="flex items-center gap-2 bg-black/40 rounded-xl px-4 py-3 font-mono text-sm">
              <code className="flex-1 overflow-x-auto whitespace-nowrap">{DOCKER_SNIPPET}</code>
              <button
                onClick={handleCopy}
                aria-label="Copy docker command"
                className="shrink-0 w-11 h-11 -my-1.5 -mr-2 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-teal" aria-hidden="true" />
                ) : (
                  <Copy className="w-4 h-4" aria-hidden="true" />
                )}
              </button>
            </div>
            {copied && <p className="mt-2 text-xs text-teal">Copied to clipboard</p>}
          </div>

          <div className="space-y-4">
            <p className="text-white/80 leading-relaxed">
              No telemetry by default: analytics only run if you configure your own PostHog key.
              Read every line of code, file issues, or send a PR.
            </p>
            <a
              href={GITHUB_REPO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => analytics.landingGithubClicked('own_your_data')}
              className="btn btn-secondary"
            >
              <Github className="w-5 h-5 mr-2" aria-hidden="true" />
              View on GitHub
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function CtaSection() {
  return (
    <section className="landing-section">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <div className="card-feature p-12 md:p-16">
          <h2 className="font-display text-display-sm md:text-display-md text-text-primary mb-6">
            Ready to Simplify Your Family's Life?
          </h2>
          <p className="text-lg text-text-secondary mb-10 max-w-2xl mx-auto">
            A better way to coordinate schedules, chores, and daily life — open source and free to self-host.
          </p>
          <Link
            to="/login"
            onClick={() => analytics.landingCtaClicked('hero')}
            className="btn btn-primary btn-lg group"
          >
            Get Started Free
            <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
          </Link>
          <p className="mt-6 text-sm text-text-muted">
            No credit card required. Free forever for basic features.
          </p>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="py-12 px-6 border-t border-border-light bg-bg-surface">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          {/* Logo and tagline */}
          <div className="flex items-center gap-4">
            <img
              src="/my-family-calendar-primary-icon.png?v=2"
              alt=""
              className="w-12 h-12 rounded-xl shadow-sm"
            />
            <div>
              <div className="font-display font-semibold text-text-primary">My Family Calendar</div>
              <div className="text-sm text-text-muted">Your household command center</div>
            </div>
          </div>

          {/* Links */}
          <nav className="flex flex-wrap items-center justify-center gap-8">
            <a href="#features" className="text-sm text-text-secondary hover:text-text-primary transition-colors">
              Features
            </a>
            <a href="#how-it-works" className="text-sm text-text-secondary hover:text-text-primary transition-colors">
              How It Works
            </a>
            <a
              href={GITHUB_REPO_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => analytics.landingGithubClicked('footer')}
              className="text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              GitHub
            </a>
            <Link
              to="/privacy"
              className="text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              Terms of Service
            </Link>
          </nav>

          {/* Copyright */}
          <div className="text-sm text-text-muted">
            &copy; {new Date().getFullYear()} My Family Calendar
          </div>
        </div>
      </div>
    </footer>
  );
}

export function LandingPage() {
  useEffect(() => {
    analytics.landingPageViewed();
  }, []);

  return (
    <div className="min-h-screen bg-bg-primary flex flex-col overflow-x-hidden">
      <Header />
      <main className="flex-1">
        <HeroSection />
        <FeaturesSection />
        <HowItWorksSection />
        <OwnYourDataSection />
        <CtaSection />
      </main>
      <Footer />
    </div>
  );
}
